<script setup lang="ts">
import {computed} from "vue";
import {useCharacterDataStore} from "@/stores/character-data";
import {useUxData} from "@/stores/ux-data.ts";
import {enteredValueFor, enterableFieldLabel} from "@/appdata/container-entries";
import type {ContainerEffect, SwadeDrawbackInterface, SwadeEdgeContainerInterface} from "@/types/items";
import type {SwadeCharacterInterface} from "@/types/characters.ts";

// The text field a row shows when a container.* trapping modification leaves the trapping to the
// player (spec 2026-09-15; shared by the edge and hindrance lists since spec 2026-10-05). The root
// element is the <input> itself, so a host's data-test and classes land on it; the host supplies
// the wrapping .trapping-entry element and decides when the field shows, through
// useTrappingEntries. Keep the template to that one element — these notes live here, not beside
// the <input>, because a root-level template comment makes the component a fragment in dev.
//
// The input's :value is one-way, and the template's own enteredValueFor read is what tracks
// `entered`, so a store write re-renders the field (useTrappingEntries' map deliberately does not
// read it). The host's row must be keyed by container uuid: keyed, the field moves with its row
// when the list reorders; unkeyed, Vue hands this instance another row's props and a half-typed
// answer is dropped.
const props = defineProps<{
  character: SwadeCharacterInterface,
  // The row object itself, never a trimmed copy of it: its uuid keys the answer, and its catalog
  // element is what enterableFieldLabel matches a chip label against.
  container: SwadeEdgeContainerInterface | SwadeDrawbackInterface,
  // The ledger entry useTrappingEntries returned for this row.
  effect: ContainerEffect,
}>();
const emit = defineEmits<{ entered: [event: Event] }>();

const ux = useUxData();
const characterData = useCharacterDataStore();

// What the field asks for: the modification's own label, else the name of the catalog element this
// row matches, else the generic word (spec 2026-09-17). Resolved once because the string is needed
// twice — the placeholder and the accessible name, the input having no <label> of its own — and
// here rather than in enterableFieldLabel because only a component can read the ux tree.
const label = computed<string>(() =>
    enterableFieldLabel(props.effect, props.container)
    ?? ux.labels?.swade?.data?.modifications?.labels?.container?.trapping
    ?? 'Trapping');

const onChange = (event: Event) => {
  // On `change` (blur / Enter), not on every keystroke: each write recomputes every override, and
  // a host's `entered` handler may trigger its view's recalculations.
  const input = event.target as HTMLInputElement;
  characterData.enterContainerEffectValue(props.character, props.effect.effectId, props.container.uuid ?? '', input.value);
  // The box then shows what was stored, not what was typed. The store trims, and a write that
  // changes nothing — whitespace typed into an empty field — triggers no re-render to tidy the
  // box, which would keep the invisible text and hide the placeholder.
  input.value = enteredValueFor(props.effect, props.container.uuid) ?? '';
  emit('entered', event);
};
</script>

<template>
  <input type="text"
         class="form-control form-control-sm"
         :placeholder="label"
         :aria-label="label"
         :value="enteredValueFor(effect, container.uuid) ?? ''"
         @change="onChange"
  />
</template>
